/**
 * PrintModal Component
 * 
 * Modal para impressão de cupom simples ou cupom fiscal (NFC-e).
 * 
 * Funcionalidades:
 * - Opção 1: Cupom Simples (Não Fiscal)
 *   - Usa react-to-print para controle de layout
 *   - Layout otimizado para impressoras térmicas 80mm
 *   - Preview antes de imprimir
 * - Opção 2: Cupom Fiscal (NFC-e)
 *   - Modal para inserção de CPF
 *   - Validação de CPF
 *   - Integração com emissão de NFC-e no backend
 *   - Geração de PDF (DANFE) no backend
 */

import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Printer, FileText, AlertCircle, CheckCircle, Loader2, ScrollText } from 'lucide-react'
import Modal from '../Modal/Modal'
import FormField from '../Form/FormField'
import { fiscalService } from '../../../services/api'
import { nfeService, customerService, saleService } from '../../../services/erpApi'
import { buildNfePaymentsFromSaleRecords } from '../../../utils/nfePaymentsFromSale'
import thermalPrintService from '../../../services/thermalPrintService'
import './PrintModal.css'

function PrintModal({
  isOpen,
  onClose,
  saleData,
  onPrintComplete
}) {
  const navigate = useNavigate()
  const [printType, setPrintType] = useState(null) // 'simple' | 'fiscal' | 'nfe'
  const [cpf, setCpf] = useState('')
  const [cpfError, setCpfError] = useState('')
  const [loading, setLoading] = useState(false)
  const [printing, setPrinting] = useState(false) // Estado específico para impressão
  const [nfceSuccess, setNfceSuccess] = useState(null) // Dados da NFC-e emitida
  const [nfceError, setNfceError] = useState(null) // Erro na emissão
  const [nfeLoading, setNfeLoading] = useState(false)
  const [nfeSuccess, setNfeSuccess] = useState(null)
  const [nfeError, setNfeError] = useState(null)
  const [printError, setPrintError] = useState(null) // Erro na impressão
  const [printSuccess, setPrintSuccess] = useState(false) // Sucesso na impressão

  /** Zera todo estado interno (nova venda ou fechar modal) */
  const resetModalState = useCallback(() => {
    setPrintType(null)
    setCpf('')
    setCpfError('')
    setLoading(false)
    setPrinting(false)
    setNfceSuccess(null)
    setNfceError(null)
    setNfeLoading(false)
    setNfeSuccess(null)
    setNfeError(null)
    setPrintError(null)
    setPrintSuccess(false)
  }, [])

  /** Ao voltar ao menu principal, limpa subfluxos NFC-e / NF-e */
  const goBackToMenu = useCallback(() => {
    setPrintType(null)
    setCpf('')
    setCpfError('')
    setLoading(false)
    setPrinting(false)
    setNfceSuccess(null)
    setNfceError(null)
    setNfeLoading(false)
    setNfeSuccess(null)
    setNfeError(null)
  }, [])

  const persistFiscalDocumentType = useCallback(async (type) => {
    if (!saleData?.id) return false
    try {
      const res = await saleService.setFiscalDocumentType(saleData.id, type)
      return res?.success !== false
    } catch (err) {
      console.error(`Erro ao gravar fiscal_document_type=${type} na venda ${saleData.id}:`, err)
      return false
    }
  }, [saleData?.id])

  const openPrintType = useCallback(
    (type) => {
      goBackToMenu()
      setPrintType(type)
    },
    [goBackToMenu]
  )

  // Cada montagem do componente começa no menu (VendasPage desmonta ao fechar)
  useEffect(() => {
    resetModalState()
  }, [resetModalState])

  // Validar CPF
  const validateCPF = (cpfValue) => {
    const numbers = cpfValue.replace(/\D/g, '')
    
    if (numbers.length !== 11) {
      return 'CPF deve conter 11 dígitos'
    }

    // Verificar se todos os dígitos são iguais
    if (/^(\d)\1{10}$/.test(numbers)) {
      return 'CPF inválido'
    }

    // Validar dígitos verificadores
    let sum = 0
    for (let i = 0; i < 9; i++) {
      sum += parseInt(numbers.charAt(i)) * (10 - i)
    }
    let digit = 11 - (sum % 11)
    if (digit >= 10) digit = 0
    if (digit !== parseInt(numbers.charAt(9))) {
      return 'CPF inválido'
    }

    sum = 0
    for (let i = 0; i < 10; i++) {
      sum += parseInt(numbers.charAt(i)) * (11 - i)
    }
    digit = 11 - (sum % 11)
    if (digit >= 10) digit = 0
    if (digit !== parseInt(numbers.charAt(10))) {
      return 'CPF inválido'
    }

    return ''
  }

  // Formatar CPF
  const formatCPF = (value) => {
    const numbers = value.replace(/\D/g, '')
    if (numbers.length <= 3) return numbers
    if (numbers.length <= 6) return `${numbers.slice(0, 3)}.${numbers.slice(3)}`
    if (numbers.length <= 9) return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6)}`
    return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6, 9)}-${numbers.slice(9, 11)}`
  }

  // Imprimir cupom simples na impressora térmica
  const handlePrintSimple = async () => {
    if (!saleData?.id) {
      setPrintError('Dados da venda não encontrados')
      return
    }

    setPrinting(true)
    setPrintError(null)
    setPrintSuccess(false)
    
    try {
      // Buscar cupom do backend (80mm)
      const response = await fiscalService.imprimirCupomSimples(saleData.id, '80mm')
      
      if (!response.success) {
        throw new Error(response.message || 'Erro ao gerar cupom')
      }

      const cupom = response.data

      // Tentar imprimir via WebUSB (Bematech)
      try {
        if (thermalPrintService.isWebUSBSupported()) {
          // Imprimir com comandos ESC/POS diretamente na impressora térmica
          await thermalPrintService.imprimir(cupom.comandosESC)
          setPrintSuccess(true)
          await persistFiscalDocumentType('cupom')
          
          // Chamar callback de sucesso
          if (onPrintComplete) {
            setTimeout(() => {
              onPrintComplete()
            }, 1000)
          }
        } else {
          // Fallback: impressão via window.print()
          thermalPrintService.imprimirTextoSimples(cupom.texto)
          setPrintSuccess(true)
          await persistFiscalDocumentType('cupom')
          
          if (onPrintComplete) {
            setTimeout(() => {
              onPrintComplete()
            }, 1000)
          }
        }
      } catch (printError) {
        console.error('Erro na impressão térmica:', printError)
        // Se falhar, tentar fallback
        try {
          thermalPrintService.imprimirTextoSimples(cupom.texto)
          setPrintSuccess(true)
          await persistFiscalDocumentType('cupom')
        } catch (fallbackError) {
          console.error('Erro no fallback:', fallbackError)
          setPrintError('Erro ao imprimir. Verifique se a impressora está conectada.')
        }
      }
    } catch (error) {
      console.error('Erro ao processar cupom simples:', error)
      setPrintError(error.response?.data?.message || error.message || 'Erro ao gerar cupom')
    } finally {
      setPrinting(false)
    }
  }

  // Emitir NFC-e
  const handleEmitNFCE = async () => {
    setCpfError('')
    setNfceError(null)
    setNfceSuccess(null)
    
    if (!cpf || cpf.replace(/\D/g, '').length === 0) {
      setCpfError('CPF é obrigatório para emissão de NFC-e')
      return
    }

    const cpfNumbers = cpf.replace(/\D/g, '')
    const error = validateCPF(cpfNumbers)
    
    if (error) {
      setCpfError(error)
      return
    }

    if (!saleData?.id) {
      setCpfError('Dados da venda não encontrados')
      return
    }

    setLoading(true)
    try {
      // Chamar API para emitir NFC-e (enviar CPF coletado)
      const response = await fiscalService.emitirNFCe(saleData.id, cpfNumbers)
      
      if (response.success) {
        setNfceSuccess(response.data)
        await persistFiscalDocumentType('nfce')
        
        // Abrir PDF da nota fiscal em nova aba
        if (response.data.nota_fiscal?.url_pdf) {
          window.open(response.data.nota_fiscal.url_pdf, '_blank')
        }
        
        // Atualizar dados da venda localmente
        if (response.data.venda) {
          Object.assign(saleData, response.data.venda)
        }
        
        // Imprimir cupom fiscal na impressora térmica
        setPrinting(true)
        try {
          const printResponse = await fiscalService.imprimirCupomFiscal(saleData.id, '80mm')
          
          if (printResponse.success) {
            const cupom = printResponse.data
            
            // Tentar imprimir via WebUSB (Bematech)
            try {
              if (thermalPrintService.isWebUSBSupported()) {
                await thermalPrintService.imprimir(cupom.comandosESC)
                setPrintSuccess(true)
              } else {
                thermalPrintService.imprimirTextoSimples(cupom.texto)
                setPrintSuccess(true)
              }
            } catch (printError) {
              console.error('Erro na impressão térmica:', printError)
              // Tentar fallback
              try {
                thermalPrintService.imprimirTextoSimples(cupom.texto)
                setPrintSuccess(true)
              } catch (fallbackError) {
                console.error('Erro no fallback:', fallbackError)
                setPrintError('NFC-e emitida, mas houve erro ao imprimir. Verifique a impressora.')
              }
            }
          }
        } catch (printError) {
          console.error('Erro ao imprimir cupom fiscal:', printError)
          setPrintError('NFC-e emitida, mas houve erro ao imprimir.')
        } finally {
          setPrinting(false)
        }
        
        // Chamar callback de sucesso
        if (onPrintComplete) {
          setTimeout(() => {
            onPrintComplete()
          }, 2000)
        }
      } else {
        setNfceError(response.message || 'Erro ao emitir NFC-e')
      }
    } catch (error) {
      console.error('Erro ao emitir NFC-e:', error)
      
      // Tratamento específico de erros
      let errorMessage = 'Erro ao emitir NFC-e'
      
      if (error.response?.data) {
        const errorData = error.response.data
        
        if (errorData.error === 'DUPLICIDADE_NOTA') {
          errorMessage = 'Esta venda já possui uma NFC-e emitida'
          // Se já tem NFC-e, mostrar dados existentes
          if (errorData.data?.chave_nfe) {
            setNfceSuccess({
              nota_fiscal: {
                chave_nfe: errorData.data.chave_nfe,
                url_pdf: errorData.data.url_pdf
              }
            })
          }
        } else if (errorData.message) {
          errorMessage = errorData.message
        } else if (errorData.error) {
          errorMessage = errorData.error
        }
      } else if (error.message) {
        errorMessage = error.message
      }
      
      setNfceError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const resolvePaymentsForNfe = async () => {
    if (saleData?.nfePaymentsSnapshot?.length) {
      return saleData.nfePaymentsSnapshot
    }
    if (saleData?.payments?.length) {
      return buildNfePaymentsFromSaleRecords(saleData.payments)
    }
    if (saleData?.id) {
      const saleRes = await saleService.getById(saleData.id)
      if (saleRes.success && saleRes.data?.payments?.length) {
        return buildNfePaymentsFromSaleRecords(saleRes.data.payments)
      }
    }
    return []
  }

  const resolveCustomerForNfe = async () => {
    const customerId = saleData?.customer_id || saleData?.customerId
    if (!customerId) {
      return { error: 'Esta venda não possui cliente vinculado. NF-e modelo 55 exige cliente com CPF/CNPJ.' }
    }

    let cpfCnpj = saleData?.customer_cpf_cnpj
    if (!cpfCnpj) {
      try {
        const customerRes = await customerService.getById(customerId)
        if (customerRes.success && customerRes.data?.cpf_cnpj) {
          cpfCnpj = customerRes.data.cpf_cnpj
        }
      } catch (err) {
        console.warn('Erro ao buscar cliente para NF-e:', err)
      }
    }

    if (!cpfCnpj) {
      return {
        error:
          'Cliente sem CPF/CNPJ cadastrado. Atualize o cadastro do cliente antes de emitir NF-e.'
      }
    }

    return { customerId, cpfCnpj }
  }

  const handleEmitNFe = async () => {
    if (!saleData?.id) {
      setNfeError('Dados da venda não encontrados')
      return
    }

    setNfeLoading(true)
    setNfeError(null)
    setNfeSuccess(null)

    try {
      const customerResult = await resolveCustomerForNfe()
      if (customerResult.error) {
        setNfeError(customerResult.error)
        return
      }

      const payments = await resolvePaymentsForNfe()
      if (!payments.length) {
        setNfeError('Nenhum pagamento encontrado para esta venda.')
        return
      }

      const response = await nfeService.emitir({
        saleId: saleData.id,
        customerId: customerResult.customerId,
        payments,
        createAccountReceivable: false
      })

      const nota = response.data?.nota_fiscal
      const nfeRecord = response.data?.nfe
      const temChave = Boolean(nota?.chave_nfe || nfeRecord?.access_key)
      const temNfeId = Boolean(nfeRecord?.id)

      if (response.success && (temChave || temNfeId)) {
        setNfeSuccess(response.data)
        await persistFiscalDocumentType('nfe')

        if (nota?.url_pdf) {
          window.open(nota.url_pdf, '_blank')
        }

        if (onPrintComplete) {
          setTimeout(() => onPrintComplete(), 1500)
        }
      } else if (response.success && response.data?.status === 'processando') {
        setNfeError(
          response.message ||
            'NF-e enviada à SEFAZ. Aguarde e consulte o status no módulo NF-e (vínculo parcial gravado).'
        )
        if (response.data?.nfe?.id) {
          setNfeSuccess({ nfe: response.data.nfe, nota_fiscal: {} })
        }
      } else {
        setNfeError(response.message || 'Erro ao emitir NF-e')
      }
    } catch (error) {
      console.error('Erro ao emitir NF-e:', error)
      const errorData = error.response?.data

      if (errorData?.error === 'DUPLICIDADE_NOTA' || error.response?.status === 409) {
        setNfeError(errorData?.message || 'Já existe NF-e para esta venda.')
        if (errorData?.data?.nfe?.id) {
          setNfeSuccess({ nfe: errorData.data.nfe, nota_fiscal: errorData.data.nfe })
        }
      } else {
        setNfeError(
          errorData?.message || error.message || 'Erro ao emitir NF-e. Verifique os dados do cliente e dos produtos.'
        )
      }
    } finally {
      setNfeLoading(false)
    }
  }

  const handleClose = () => {
    onClose()
  }

  if (!isOpen) return null

  return (
    <>
      {/* Modal de Seleção de Tipo de Impressão */}
      {!printType && (
        <Modal
          isOpen={isOpen}
          onClose={handleClose}
          title="Documentos da venda"
          size="large"
          footer={
            <>
              <button className="btn-secondary" onClick={handleClose}>
                Cancelar
              </button>
            </>
          }
        >
          <div className="print-options">
            <button
              className="print-option-card"
              onClick={handlePrintSimple}
              disabled={printing || loading}
            >
              <Printer size={32} />
              <h3>Cupom Simples</h3>
              <p>
                {printing ? 'Imprimindo na impressora térmica...' : 
                 printSuccess ? '✓ Impresso com sucesso!' :
                 printError ? '✗ Erro ao imprimir' :
                 'Impressão não fiscal direta'}
              </p>
              {printError && (
                <p style={{ color: '#ef4444', fontSize: '0.75rem', marginTop: '0.5rem' }}>
                  {printError}
                </p>
              )}
            </button>

            <button
              className="print-option-card"
              onClick={() => openPrintType('fiscal')}
            >
              <FileText size={32} />
              <h3>Cupom Fiscal (NFC-e)</h3>
              <p>Nota ao consumidor (modelo 65)</p>
            </button>

            <button
              className="print-option-card print-option-card-nfe"
              onClick={() => openPrintType('nfe')}
            >
              <ScrollText size={32} />
              <h3>NF-e Modelo 55</h3>
              <p>Nota fiscal para pessoa jurídica / CNPJ</p>
            </button>
          </div>
        </Modal>
      )}

      {/* Modal NF-e modelo 55 */}
      {printType === 'nfe' && (
        <Modal
          isOpen={true}
          onClose={goBackToMenu}
          title="Emitir NF-e (modelo 55)"
          size="medium"
          footer={
            <>
              <button
                type="button"
                className="btn-secondary"
                onClick={goBackToMenu}
                disabled={nfeLoading}
              >
                Voltar
              </button>
              {nfeSuccess?.nfe?.id && (
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => navigate(`/erp/vendas/nfe/${nfeSuccess.nfe.id}/editar`)}
                >
                  Abrir no módulo NF-e
                </button>
              )}
              <button
                type="button"
                className="btn-primary"
                onClick={handleEmitNFe}
                disabled={nfeLoading || !!nfeSuccess?.nota_fiscal?.chave_nfe}
              >
                {nfeLoading ? (
                  <>
                    <Loader2 size={16} className="spin-animation" style={{ marginRight: '0.5rem' }} />
                    Emitindo...
                  </>
                ) : nfeSuccess ? (
                  <>
                    <CheckCircle size={16} style={{ marginRight: '0.5rem' }} />
                    Emitida
                  </>
                ) : (
                  'Emitir NF-e'
                )}
              </button>
            </>
          }
        >
          <div style={{ padding: '0.5rem 0' }}>
            <div
              style={{
                padding: '0.75rem',
                background: '#eff6ff',
                borderRadius: '6px',
                marginBottom: '1rem',
                fontSize: '0.875rem',
                color: '#1e40af'
              }}
            >
              <strong>NF-e modelo 55</strong> — indicada para vendas com cliente identificado (CPF/CNPJ).
              O estoque já foi baixado na finalização do caixa; as contas a receber do crediário não serão
              duplicadas.
            </div>

            {saleData?.customer_name && (
              <div
                style={{
                  marginBottom: '1rem',
                  padding: '0.75rem',
                  background: '#f9fafb',
                  borderRadius: '6px',
                  fontSize: '0.875rem'
                }}
              >
                <strong>Cliente:</strong> {saleData.customer_name}
                {saleData.customer_cpf_cnpj && (
                  <div style={{ color: '#6b7280', marginTop: '0.25rem' }}>
                    CPF/CNPJ: {saleData.customer_cpf_cnpj}
                  </div>
                )}
              </div>
            )}

            {nfeSuccess && (
              <div
                style={{
                  padding: '1rem',
                  background: '#d1fae5',
                  borderRadius: '6px',
                  marginBottom: '1rem',
                  fontSize: '0.875rem',
                  color: '#065f46'
                }}
              >
                <CheckCircle size={20} style={{ verticalAlign: 'middle', marginRight: '0.5rem' }} />
                <strong>NF-e emitida com sucesso!</strong>
                {nfeSuccess.nota_fiscal?.numero && (
                  <div style={{ marginTop: '0.25rem' }}>
                    Número: {nfeSuccess.nota_fiscal.numero}
                    {nfeSuccess.nota_fiscal.serie ? ` / Série ${nfeSuccess.nota_fiscal.serie}` : ''}
                  </div>
                )}
                {nfeSuccess.nota_fiscal?.chave_nfe && (
                  <div style={{ marginTop: '0.25rem', fontSize: '0.75rem', wordBreak: 'break-all' }}>
                    Chave: {nfeSuccess.nota_fiscal.chave_nfe}
                  </div>
                )}
                {nfeSuccess.nota_fiscal?.url_pdf && (
                  <div style={{ marginTop: '0.5rem' }}>
                    <a
                      href={nfeSuccess.nota_fiscal.url_pdf}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#059669', fontWeight: 600 }}
                    >
                      Abrir DANFE (PDF)
                    </a>
                  </div>
                )}
              </div>
            )}

            {nfeError && (
              <div
                style={{
                  padding: '1rem',
                  background: '#fee2e2',
                  borderRadius: '6px',
                  marginBottom: '1rem',
                  fontSize: '0.875rem',
                  color: '#991b1b',
                  display: 'flex',
                  gap: '0.5rem'
                }}
              >
                <AlertCircle size={20} style={{ flexShrink: 0 }} />
                <div>{nfeError}</div>
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* Modal de CPF para NFC-e */}
      {printType === 'fiscal' && (
        <Modal
          isOpen={true}
          onClose={goBackToMenu}
          title="Emitir NFC-e"
          size="small"
          footer={
            <>
              <button
                className="btn-secondary"
                onClick={goBackToMenu}
                disabled={loading}
              >
                Voltar
              </button>
              <button
                className="btn-primary"
                onClick={handleEmitNFCE}
                disabled={loading || printing || !cpf || cpfError || !!nfceSuccess}
              >
                {loading || printing ? (
                  <>
                    <Loader2 
                      size={16} 
                      className="spin-animation"
                      style={{ marginRight: '0.5rem' }}
                    />
                    {loading ? 'Emitindo...' : 'Imprimindo...'}
                  </>
                ) : nfceSuccess ? (
                  <>
                    <CheckCircle size={16} style={{ marginRight: '0.5rem' }} />
                    {printSuccess ? 'Emitida e Impressa' : 'Emitida'}
                  </>
                ) : (
                  'Emitir NFC-e'
                )}
              </button>
            </>
          }
        >
          <div style={{ padding: '1rem 0' }}>
            <div style={{ 
              padding: '0.75rem', 
              background: '#fef3c7', 
              borderRadius: '6px', 
              marginBottom: '1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '0.875rem',
              color: '#92400e'
            }}>
              <AlertCircle size={18} />
              <span>CPF é obrigatório para emissão de NFC-e</span>
            </div>

            {/* Mensagem de sucesso */}
            {nfceSuccess && (
              <div style={{ 
                padding: '1rem', 
                background: '#d1fae5', 
                borderRadius: '6px', 
                marginBottom: '1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                fontSize: '0.875rem',
                color: '#065f46'
              }}>
                <CheckCircle size={20} />
                <div style={{ flex: 1 }}>
                  <strong>NFC-e emitida com sucesso!</strong>
                  {nfceSuccess.nota_fiscal?.chave_nfe && (
                    <div style={{ marginTop: '0.25rem', fontSize: '0.75rem' }}>
                      Chave de Acesso: {nfceSuccess.nota_fiscal.chave_nfe}
                    </div>
                  )}
                  {nfceSuccess.nota_fiscal?.url_pdf && (
                    <div style={{ marginTop: '0.5rem' }}>
                      <a 
                        href={nfceSuccess.nota_fiscal.url_pdf} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{ 
                          color: '#059669',
                          textDecoration: 'underline',
                          fontWeight: '500'
                        }}
                      >
                        Abrir DANFC-e (PDF)
                      </a>
                    </div>
                  )}
                  {printSuccess && (
                    <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: '#059669' }}>
                      ✓ Cupom fiscal impresso na impressora térmica
                    </div>
                  )}
                  {printError && (
                    <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: '#dc2626' }}>
                      ⚠ {printError}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Mensagem de erro */}
            {nfceError && (
              <div style={{ 
                padding: '1rem', 
                background: '#fee2e2', 
                borderRadius: '6px', 
                marginBottom: '1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                fontSize: '0.875rem',
                color: '#991b1b'
              }}>
                <AlertCircle size={20} />
                <div style={{ flex: 1 }}>
                  <strong>Erro ao emitir NFC-e:</strong>
                  <div style={{ marginTop: '0.25rem' }}>{nfceError}</div>
                </div>
              </div>
            )}

            <FormField
              label="CPF do Cliente"
              name="cpf"
              type="text"
              value={cpf}
              onChange={(e) => {
                const formatted = formatCPF(e.target.value)
                setCpf(formatted)
                setCpfError('')
                setNfceError(null)
              }}
              error={cpfError}
              placeholder="000.000.000-00"
              required
              maxLength={14}
              disabled={loading || !!nfceSuccess}
            />

            {saleData?.customer_name && (
              <div style={{ 
                marginTop: '1rem', 
                padding: '0.75rem', 
                background: '#f3f4f6', 
                borderRadius: '6px',
                fontSize: '0.875rem',
                color: '#6b7280'
              }}>
                <strong>Cliente:</strong> {saleData.customer_name}
              </div>
            )}

            {/* Informação sobre venda já com NFC-e */}
            {saleData?.chave_nfe && !nfceSuccess && (
              <div style={{ 
                marginTop: '1rem', 
                padding: '0.75rem', 
                background: '#fef3c7', 
                borderRadius: '6px',
                fontSize: '0.875rem',
                color: '#92400e',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <AlertCircle size={18} />
                <div>
                  <strong>Esta venda já possui NFC-e emitida.</strong>
                  {saleData.url_pdf && (
                    <div style={{ marginTop: '0.25rem' }}>
                      <a 
                        href={saleData.url_pdf} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{ 
                          color: '#92400e',
                          textDecoration: 'underline'
                        }}
                      >
                        Ver DANFC-e
                      </a>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </Modal>
      )}
    </>
  )
}

export default PrintModal

